import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { Session, User, Provider } from '@supabase/supabase-js'
import { supabase } from '../hooks/supabase'
import { useNavigate } from '@tanstack/react-router'

interface SupabaseContextType {
  user: User | null
  session: Session | null
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, name: string) => Promise<void>
  signOut: () => Promise<void>
  signInWithOAuth: (provider: Provider, returnTo: string) => Promise<(() => void) | void>
  signInWithOTP: (email: string) => Promise<void>
  verifyOTP: (email: string, token: string) => Promise<void>
  isLoading: boolean
  error: string | null
}

const SupabaseContext = createContext<SupabaseContextType>({
  user: null,
  session: null,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  signInWithOAuth: async () => {},
  signInWithOTP: async () => {},
  verifyOTP: async () => {},
  isLoading: true,
  error: null
})

export function SupabaseProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setIsLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [navigate])

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      if (error) throw error
    } catch (error) {
      console.error('Error signing in:', error)
      throw error
    }
  }

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const domain = email.split('@')[1]
      const commonPersonalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com']
      
      if (commonPersonalDomains.includes(domain)) {
        throw new Error('Please use your business email address to sign up')
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name
          }
        }
      })
      if (error) throw error
      
      navigate({ to: '/onboard' })
    } catch (error) {
      console.error('Error signing up:', error)
      throw error
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      navigate({ to: '/signin' })
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }

  const signInWithOAuth = async (provider: Provider, returnTo: string = '/') => {
    try {
      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}${returnTo}`
        }
      });

      if (signInError) throw signInError;

    } catch (error) {
      console.error('Error signing in with OAuth:', error);
      throw error;
    }
  }

  const signInWithOTP = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: undefined
        }
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error sending OTP:', error);
      throw error;
    }
  };

  const verifyOTP = async (email: string, token: string) => {
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email'
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error verifying OTP:', error);
      throw error;
    }
  };

  return (
    <SupabaseContext.Provider value={{ 
      user, 
      session, 
      signIn, 
      signUp, 
      signOut, 
      signInWithOAuth,
      signInWithOTP,
      verifyOTP,
      isLoading,
      error
    }}>
      {children}
    </SupabaseContext.Provider>
  )
}

export const useSupabase = () => useContext(SupabaseContext)
